import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ChannelPreview.css';

// "What you get" mockup shown at the top of a Deploy → Channels modal.
//
// Drawn in markup at the surfaces' REAL pixel sizes and then scaled down as a
// whole (`transform: scale`), so every proportion — panel size, header height,
// bubble radius, type scale — matches what actually ships. Nothing here is a
// screenshot: it re-reads the bot's own `widget_configuration`, so the owner
// sees their colour, header text and welcome message, not a generic stock
// image. Numbers below are annotated with the file they were taken from; if a
// surface changes, change them here in the same PR.
//
// Three variants, matching the three generic website channels:
//   'widget'    floating launcher + panel on the customer's page
//               (ultimo-bots-widget/src/index.js — panel 430x620, launcher 75)
//   'iframe'    the hosted chat embedded inline in a page, 600px tall
//               (the height in ChannelGuide's iframe snippet)
//   'sharelink' the same hosted chat as its own browser tab
//               (ultimo-bots-frontend/src/Chat.css)
// 'iframe' and 'sharelink' render the SAME surface on purpose: both point at
// chat.ultimo-bots.com/<botId>. Only the frame around it differs.

// The simulated desktop viewport. 16:9 so the band has a predictable height.
const STAGE_W = 1440;
const STAGE_H = 810;

// Widget runtime defaults, mirrored from ultimo-bots-widget/src/index.js:2243-2265.
// These stay untranslated on purpose: they are the literal strings a visitor
// would see if the owner never changed them, so translating them would make
// the preview lie. The invented sample dialogue below DOES go through t().
const DEFAULTS = {
  theme: '#0082ba',
  headerFont: '#ffffff',
  headerText: 'Chat with us!',
  placeholder: 'Type your message...',
  size: 75,
  radius: 50,
};

// One in-flight request per bot, shared across all three modals — opening
// widget → iframe → chat link in a row hits the network once.
const configCache = new Map();

function loadWidgetConfig(botId) {
  if (!botId) return Promise.resolve(null);
  const key = String(botId);
  if (!configCache.has(key)) {
    configCache.set(
      key,
      fetch(`${process.env.REACT_APP_API_BASE_URL}widget_configuration/${key}`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    );
  }
  return configCache.get(key);
}

function resolveConfig(raw) {
  const c = raw || {};
  const welcomeRaw = Array.isArray(c.welcome_message)
    ? c.welcome_message[0]
    : c.welcome_message;
  return {
    theme: c.theme_color || DEFAULTS.theme,
    headerFont: c.header_font_color || DEFAULTS.headerFont,
    headerText: c.header_text || DEFAULTS.headerText,
    // Empty is meaningful: the widget then renders no welcome bubble at all.
    // The component substitutes a translated placeholder so the preview is
    // not an empty panel.
    welcome: (welcomeRaw || '').trim(),
    placeholder: c.input_placeholder || DEFAULTS.placeholder,
    // Both surfaces render the header logo and the message avatar ONLY when
    // the owner uploaded one — no element, no placeholder circle (widget
    // index.js createMessageRow / logoHTML, frontend Chat.js:677 + :806). The
    // preview does the same, so an owner without an avatar sees exactly what
    // their visitors see.
    logo: c.header_icon_path || null,
    avatar: c.avatar_icon_path || null,
    size: c.widget_size ?? DEFAULTS.size,
    radius: c.widget_border_radius ?? DEFAULTS.radius,
    // widget/src/index.js:2296 — anything other than an explicit 'left' is right.
    alignLeft: c.widget_horizontal_alignment === 'left',
  };
}

/* ── Icons (paths lifted from the shipped surfaces) ──────────────────── */

// widget/src/index.js disclosureHTML + frontend Chat.js
const InfoIcon = ({ className, color }) => (
  <svg className={className} viewBox="0 0 512 512" fill={color} aria-hidden="true">
    <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336l24 0 0-64-24 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l48 0c13.3 0 24 10.7 24 24l0 88 8 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-80 0c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z" />
  </svg>
);

// The send button's arrow-up, same path both surfaces use.
const SendIcon = ({ color }) => (
  <svg className="cp-send-icon" viewBox="0 0 384 512" fill={color} aria-hidden="true">
    <path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0L7 203.6c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 140.3V464c0 17.7 14.3 32 32 32s32-14.3 32-32V140.3l107.6 108.7c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L214.6 41.4z" />
  </svg>
);

// Launcher glyph while the panel is open: the widget cross-rotates its normal
// face out and this chevron in (index.js `.saicf-icon-close`).
const ChevronDownIcon = ({ color }) => (
  <svg className="cp-launcher-glyph" viewBox="0 0 512 512" fill={color} aria-hidden="true">
    <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z" />
  </svg>
);

const EllipsisIcon = ({ color }) => (
  <svg className="cp-ellipsis" viewBox="0 0 448 512" fill={color} aria-hidden="true">
    <path d="M120 256a56 56 0 1 1 -112 0 56 56 0 1 1 112 0zm160 0a56 56 0 1 1 -112 0 56 56 0 1 1 112 0zm104 56a56 56 0 1 1 0-112 56 56 0 1 1 0 112z" />
  </svg>
);

/* ── The chat surface ─────────────────────────────────────────────────── */

// `flavour` picks which shipped surface we are drawing:
//   'widget'  the Shadow-DOM panel  (saicf-* metrics, index.js)
//   'hosted'  chat.ultimo-bots.com  (chat-* metrics, Chat.css)
// They are NOT the same surface — most visibly, the widget paints the visitor's
// own bubble in the brand colour while the hosted page keeps it neutral grey.
function ChatSurface({ cfg, flavour }) {
  const { t } = useTranslation();
  const widget = flavour === 'widget';
  // Cover-filled so a non-square upload crops instead of squashing — same
  // `background-size: cover` / `object-fit: cover` the two surfaces use.
  const avatarEl = cfg.avatar ? (
    <span className="cp-avatar" style={{ backgroundImage: `url("${cfg.avatar}")` }} />
  ) : null;
  return (
    <div className={`cp-surface cp-surface-${flavour}`}>
      <div className="cp-head" style={{ background: cfg.theme, color: cfg.headerFont }}>
        <div className="cp-head-row">
          <div className="cp-head-left">
            {cfg.logo && <img className="cp-head-logo" src={cfg.logo} alt="" />}
            <span className="cp-head-title">{cfg.headerText}</span>
          </div>
          <EllipsisIcon color={cfg.headerFont} />
        </div>
      </div>

      {/* Always-on AI notice. Every visitor sees this before they can type. */}
      <div className="cp-disclosure">
        <InfoIcon className="cp-disclosure-icon" color={cfg.theme} />
        <span className="cp-disclosure-text">{t('cpreview_disclosure')}</span>
      </div>

      {/* Sample exchange, deliberately industry-neutral (hours + a callback ask,
          which is also where lead capture would kick in) so it fits whatever
          the bot is actually for. Only the first bubble is real data. */}
      <div className="cp-body">
        <div className="cp-row cp-row-bot">
          {avatarEl}
          <span className="cp-msg cp-msg-bot">
            {cfg.welcome || t('cpreview_welcome_fallback')}
          </span>
        </div>
        <div className="cp-row cp-row-user">
          <span
            className="cp-msg cp-msg-user"
            style={widget ? { background: cfg.theme, color: '#fff' } : undefined}
          >
            {t('cpreview_q1')}
          </span>
        </div>
        <div className="cp-row cp-row-bot">
          {avatarEl}
          <span className="cp-msg cp-msg-bot">{t('cpreview_a1')}</span>
        </div>
        <div className="cp-row cp-row-user">
          <span
            className="cp-msg cp-msg-user"
            style={widget ? { background: cfg.theme, color: '#fff' } : undefined}
          >
            {t('cpreview_q2')}
          </span>
        </div>
        <div className="cp-row cp-row-bot">
          {avatarEl}
          <span className="cp-msg cp-msg-bot">{t('cpreview_a2')}</span>
        </div>
      </div>

      <div className="cp-foot">
        <div className="cp-powered">Powered by Ultimo Bots</div>
        <div className="cp-input-row">
          <div className="cp-input">{cfg.placeholder}</div>
          <div className="cp-send" style={{ background: cfg.theme }}>
            <SendIcon color={cfg.headerFont} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Browser chrome + abstract host page ──────────────────────────────── */

function BrowserChrome({ url, children }) {
  return (
    <div className="cp-browser">
      <div className="cp-browser-bar">
        <span className="cp-dot" />
        <span className="cp-dot" />
        <span className="cp-dot" />
        <div className="cp-url">{url}</div>
      </div>
      <div className="cp-viewport">{children}</div>
    </div>
  );
}

// Deliberately abstract grey blocks: this stands for "your website", so it must
// not look like one particular site.
function SiteSkeleton({ children }) {
  return (
    <div className="cp-site">
      {/* Nav links sit next to the logo, not flushed right: on the widget
          variant the open panel covers the right third of the page (which is
          exactly what happens on a real laptop viewport), and a nav that is
          entirely hidden behind it reads as a rendering bug. */}
      <div className="cp-site-nav">
        <span className="cp-nav-logo" />
        <span className="cp-nav-links">
          <span className="cp-nav-link" />
          <span className="cp-nav-link" />
          <span className="cp-nav-link" />
        </span>
        <span className="cp-nav-cta" />
      </div>
      <div className="cp-site-main">{children}</div>
    </div>
  );
}

function ChannelPreview({ variant, botId }) {
  const { t } = useTranslation();
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(null);
  const [cfg, setCfg] = useState(() => resolveConfig(null));

  useEffect(() => {
    let alive = true;
    loadWidgetConfig(botId).then((raw) => {
      if (alive && raw) setCfg(resolveConfig(raw));
    });
    return () => {
      alive = false;
    };
  }, [botId]);

  // The stage is drawn at 1440x810 and scaled to whatever width the modal
  // gives us. Measured rather than hard-coded because `.channel-modal` is
  // 780px on desktop but `max-width: 100%` below that.
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setScale(el.clientWidth / STAGE_W);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const launcherStyle = {
    width: cfg.size,
    height: cfg.size,
    background: cfg.theme,
    borderRadius: `${cfg.radius}%`,
    [cfg.alignLeft ? 'left' : 'right']: 20,
  };
  // index.js:1415 — the panel opens above the launcher: 32px + launcher size.
  const panelStyle = {
    bottom: 32 + cfg.size,
    [cfg.alignLeft ? 'left' : 'right']: 20,
  };

  return (
    <div className="cpreview" ref={wrapRef}>
      <div
        className="cpreview-stage"
        style={{
          width: STAGE_W,
          height: STAGE_H,
          transform: `scale(${scale ?? 0})`,
          visibility: scale == null ? 'hidden' : 'visible',
        }}
        aria-hidden="true"
      >
        {variant === 'widget' && (
          <BrowserChrome url={t('cpreview_url_site')}>
            <SiteSkeleton>
              <span className="cp-bar cp-bar-xl" />
              <span className="cp-bar cp-bar-lg" />
              <span className="cp-line" />
              <span className="cp-line cp-line-short" />
              <div className="cp-cards">
                <span className="cp-card" />
                <span className="cp-card" />
                <span className="cp-card" />
              </div>
              {/* A second section below the fold-ish line, so the page reads as
                  a real page rather than a mostly empty frame. */}
              <span className="cp-bar cp-bar-lg" />
              <span className="cp-line" />
              <span className="cp-line cp-line-short" />
            </SiteSkeleton>
            <div className="cp-panel" style={panelStyle}>
              <ChatSurface cfg={cfg} flavour="widget" />
            </div>
            <div className="cp-launcher" style={launcherStyle}>
              <ChevronDownIcon color={cfg.headerFont} />
            </div>
          </BrowserChrome>
        )}

        {variant === 'iframe' && (
          <BrowserChrome url={t('cpreview_url_page')}>
            <SiteSkeleton>
              <span className="cp-bar cp-bar-lg" />
              <span className="cp-line cp-line-short" />
              <div className="cp-embed">
                <ChatSurface cfg={cfg} flavour="hosted" />
              </div>
              <span className="cp-line" />
            </SiteSkeleton>
          </BrowserChrome>
        )}

        {variant === 'sharelink' && (
          <BrowserChrome url={`chat.ultimo-bots.com/${botId || ''}`}>
            <div className="cp-standalone">
              <ChatSurface cfg={cfg} flavour="hosted" />
            </div>
          </BrowserChrome>
        )}
      </div>
    </div>
  );
}

export default ChannelPreview;
